import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaShareAlt, FaSyncAlt } from 'react-icons/fa';
import SharingRuleApi from '../../../api/SharingRuleApi';
import SharingApi from '../../../api/SharingApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import { getEntitlementsSection } from '../../../utils/entitlements';
import SharingRuleCriteriaConfig, { SharingCriteriaLogicSelect } from './SharingRuleCriteriaConfig';
import { useConditionValueFields } from '../../../hooks/useConditionValueFields';
import { FILTER_VALUE_TYPE_STATIC } from '../../../utils/filterFieldValueUtils';
import LookupLayout from '../../layout/LookupLayout';
import {
  standardInputStyles,
  standardInputContainerHeight,
} from '../../../utils/conditionInputStyles';
import './SharingRuleManager.css';

const emptyAccessRow = () => ({
  target_type: 'USER',
  target_id: '',
  target_label: '',
  access_level: 'read',
});

/** Map API / DB values to form select values. */
function normalizeAccessLevelForForm(level) {
  const raw = String(level ?? '').trim();
  if (!raw) return 'read';
  const lower = raw.toLowerCase().replace(/-/g, '_');
  if (lower === 'read_write_delete' || lower === 'full') return 'read_write_delete';
  if (lower === 'read_write' || lower === 'write') return 'read_write';
  if (lower === 'read') return 'read';
  const upper = raw.toUpperCase();
  if (upper === 'READ_WRITE_DELETE' || upper === 'FULL') return 'read_write_delete';
  if (upper === 'WRITE' || upper === 'READ_WRITE') return 'read_write';
  if (upper === 'READ') return 'read';
  return 'read';
}

const SHARING_ACCESS_LEVEL_OPTIONS = [
  { value: 'read', label: 'Read' },
  { value: 'read_write', label: 'Read / Write' },
  { value: 'read_write_delete', label: 'Read / Write / Delete' },
];

/** Lookup API object + fields for sharing grant targets (matches assignment / workflow patterns). */
function grantLookupMeta(targetType) {
  switch (String(targetType || '').toUpperCase()) {
    case 'ROLE':
      return {
        objectName: 'role',
        searchFields: 'name',
        displayFields: 'name',
        placeholder: 'Search roles…',
        lookupFilter: [{ field: 'is_active', operator: 'equals', value: 'true' }],
      };
    case 'TEAM':
      return {
        objectName: 'user_group',
        searchFields: 'name,description',
        displayFields: 'name',
        placeholder: 'Search user groups…',
        lookupFilter: [{ field: 'is_active', operator: 'equals', value: 'true' }],
      };
    case 'USER':
    default:
      return {
        objectName: 'user',
        searchFields: 'firstname,lastname,email,username',
        displayFields: 'firstname,lastname',
        placeholder: 'Search users…',
        activeUsersOnly: true,
      };
  }
}

const emptyCriterion = () => ({
  fieldName: '',
  operator: '',
  value: '',
  valueType: FILTER_VALUE_TYPE_STATIC,
});

function normalizeCriterionRow(c) {
  if (!c || typeof c !== 'object') return emptyCriterion();
  return {
    fieldName: c.fieldName || c.field || '',
    operator: c.operator || '',
    value: c.value !== undefined && c.value !== null ? c.value : '',
    valueType: c.valueType || c.value_type || FILTER_VALUE_TYPE_STATIC,
  };
}

/** Default numbered expression when switching to custom criteria logic. */
function defaultSharingCustomExpression(validCriteriaCount) {
  if (validCriteriaCount <= 0) return '1';
  if (validCriteriaCount === 1) return '1';
  return Array.from({ length: validCriteriaCount }, (_, i) => String(i + 1)).join(' AND ');
}

/** Parse API conditions_json into builder state (structured rules only; other shapes default empty). */
function parseConditionsState(raw) {
  const empty = {
    logic: 'AND',
    criteriaList: [emptyCriterion()],
    customLogic: '',
  };
  let cj = raw;
  if (typeof cj === 'string') {
    try {
      cj = JSON.parse(cj);
    } catch {
      return empty;
    }
  }
  if (!cj || typeof cj !== 'object' || Array.isArray(cj)) {
    return empty;
  }

  if (Array.isArray(cj.criteria)) {
    const criteriaList =
      cj.criteria.length > 0
        ? cj.criteria.map(normalizeCriterionRow)
        : [emptyCriterion()];
    const logicUpper = String(cj.logic || '').toUpperCase();
    const logic =
      logicUpper === 'CUSTOM' ? 'CUSTOM' : logicUpper === 'OR' ? 'OR' : 'AND';
    return {
      logic,
      criteriaList,
      customLogic: typeof cj.customLogic === 'string' ? cj.customLogic : '',
    };
  }

  return empty;
}

function canViewSharingRulesPage() {
  const setupConfig = getEntitlementsSection('setup', {});
  const allowed = new Set(
    (setupConfig?.allowedItems || []).map((s) => String(s).toLowerCase())
  );
  const mode = setupConfig?.mode;
  if (mode === 'allowlist') {
    return allowed.has('sharingrules') || allowed.has('assignmentrule');
  }
  if (mode === 'denylist') {
    return !allowed.has('sharingrules') && !allowed.has('assignmentrule');
  }
  return true;
}

function isCustomSharingConditionsJson(rule) {
  let cj = rule?.conditions_json;
  if (typeof cj === 'string') {
    try {
      cj = JSON.parse(cj);
    } catch {
      return false;
    }
  }
  return cj && typeof cj === 'object' && String(cj.logic || '').toUpperCase() === 'CUSTOM';
}

function countCriteriaFromRule(rule) {
  let cj = rule?.conditions_json;
  if (typeof cj === 'string') {
    try {
      cj = JSON.parse(cj);
    } catch {
      return 0;
    }
  }
  if (cj && typeof cj === 'object' && String(cj.logic || '').toUpperCase() === 'CUSTOM') {
    const crit = Array.isArray(cj.criteria) ? cj.criteria : [];
    return crit.filter((c) => (c.fieldName || c.field) && c.operator).length || 'custom';
  }
  if (cj && typeof cj === 'object' && Array.isArray(cj.criteria)) {
    return cj.criteria.filter((c) => (c.fieldName || c.field) && c.operator).length;
  }
  if (cj && typeof cj === 'object' && Object.keys(cj).length > 0) {
    return 'custom';
  }
  return 0;
}

const SharingRuleManager = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterObject, setFilterObject] = useState('all');
  const [activeOnly, setActiveOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  const [formObject, setFormObject] = useState('');
  const [formRuleName, setFormRuleName] = useState('');
  const [formPriority, setFormPriority] = useState(0);
  const [formActive, setFormActive] = useState(true);
  const [formAccess, setFormAccess] = useState([emptyAccessRow()]);

  const [objectFields, setObjectFields] = useState([]);
  const { valueFields: criteriaValueFields } = useConditionValueFields(formObject);
  const [criteriaLogic, setCriteriaLogic] = useState('AND');
  const [criteriaList, setCriteriaList] = useState([emptyCriterion()]);
  const [customLogic, setCustomLogic] = useState('');

  const api = useMemo(() => new SharingRuleApi(), []);
  const sharingApi = useMemo(() => new SharingApi(), []);

  const [applyModal, setApplyModal] = useState({
    show: false,
    ruleId: null,
    ruleName: '',
    objectName: '',
  });
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyError, setApplyError] = useState('');

  useEffect(() => {
    if (!canViewSharingRulesPage()) {
      navigate('/setup', { replace: true });
      return;
    }
    loadObjects();
  }, [navigate]);

  useEffect(() => {
    if (!canViewSharingRulesPage()) return;
    loadRules();
  }, [filterObject, activeOnly]);

  useEffect(() => {
    if (!showModal || !formObject) {
      setObjectFields([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(formObject);
        if (!cancelled && response?.success && Array.isArray(response.data?.fields)) {
          setObjectFields(response.data.fields);
        } else if (!cancelled) {
          setObjectFields([]);
        }
      } catch (e) {
        if (!cancelled) setObjectFields([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, formObject]);

  const loadObjects = async () => {
    try {
      const response = await ObjectConfigApi.fetchObjectConfigs();
      if (response?.success) {
        const list = (response.data || [])
          .filter((o) => !o.is_deleted)
          .map((o) => ({
            name: o.object_name,
            label: o.object_label || o.object_name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setObjects(list);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadRules = async () => {
    setLoading(true);
    setError('');
    try {
      const objectName = filterObject === 'all' ? null : filterObject;
      const res = await api.fetchAll(objectName, activeOnly);
      const payload = res?.data;
      if (payload?.success) {
        setRules(payload.data || []);
      } else {
        setError(payload?.message || 'Failed to load sharing rules');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load sharing rules');
    } finally {
      setLoading(false);
    }
  };

  const applyConditionsState = useCallback((state) => {
    setCriteriaLogic(state.logic);
    setCriteriaList(state.criteriaList);
    setCustomLogic(state.customLogic);
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormObject(objects[0]?.name || '');
    setFormRuleName('');
    setFormPriority(0);
    setFormActive(true);
    applyConditionsState({
      logic: 'AND',
      criteriaList: [emptyCriterion()],
      customLogic: '',
    });
    setFormAccess([emptyAccessRow()]);
    setModalError('');
    setShowModal(true);
    setSuccess('');
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setFormObject(rule.object_name);
    setFormRuleName(rule.name != null ? String(rule.name) : '');
    setFormPriority(rule.priority ?? 0);
    setFormActive(!!rule.is_active);
    applyConditionsState(parseConditionsState(rule.conditions_json));
    const entries = Array.isArray(rule.access_entries) ? rule.access_entries : [];
    setFormAccess(
      entries.length
        ? entries.map((a) => ({
            target_type: a.target_type || 'USER',
            target_id: a.target_id || '',
            target_label: String(a.target_label || '').trim(),
            access_level: normalizeAccessLevelForForm(a.access_level),
          }))
        : [emptyAccessRow()]
    );
    setModalError('');
    setShowModal(true);
    setSuccess('');
  };

  const closeModal = () => {
    if (saving) return;
    setModalError('');
    setShowModal(false);
  };

  const openApplyModal = (rule) => {
    if (!rule?.id) return;
    setApplyError('');
    setApplyModal({
      show: true,
      ruleId: rule.id,
      ruleName: (rule.name && String(rule.name).trim()) || rule.object_name || 'Rule',
      objectName: rule.object_name || '',
    });
  };

  const closeApplyModal = () => {
    if (applyLoading) return;
    setApplyModal({ show: false, ruleId: null, ruleName: '', objectName: '' });
    setApplyError('');
  };

  const runApplySharing = async () => {
    if (!applyModal.ruleId) return;
    setApplyLoading(true);
    setApplyError('');
    try {
      const res = await sharingApi.applyRule(applyModal.ruleId);
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || 'Apply failed');
      }
      const d = res.data.data || {};
      setSuccess(
        `Sharing applied for "${applyModal.ruleName}": ${d.matched_record_count ?? 0} records matched, ${d.upsert_rows ?? 0} access rows written.`
      );
      closeApplyModal();
    } catch (e) {
      setApplyError(e.response?.data?.message || e.message || 'Apply failed');
    } finally {
      setApplyLoading(false);
    }
  };

  const addCriterion = () => {
    setCriteriaList((prev) => [...(Array.isArray(prev) ? prev : []), emptyCriterion()]);
  };

  const deleteCriterion = (index) => {
    setCriteriaList((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [emptyCriterion()];
    });
  };

  const patchCriterion = (index, patch) => {
    setCriteriaList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...patch };
      return updated;
    });
  };

  const updateCriterion = (index, key, value) => {
    patchCriterion(index, { [key]: value });
  };

  const onSharingCriteriaLogicChange = (v) => {
    const validCount = (criteriaList || []).filter((c) => c.fieldName && c.operator).length;
    if (v === 'CUSTOM') {
      setCriteriaLogic('CUSTOM');
      setCustomLogic((prev) =>
        String(prev || '').trim() ? prev : defaultSharingCustomExpression(validCount)
      );
    } else {
      setCriteriaLogic(v);
      setCustomLogic('');
    }
  };

  const buildConditionsJson = () => {
    if (criteriaLogic === 'CUSTOM') {
      const text = String(customLogic || '').trim();
      if (!text) {
        throw new Error('Expression is required when criteria logic is Custom Logic (Expression)');
      }
      const criteria = (criteriaList || [])
        .map(normalizeCriterionRow)
        .filter((c) => c.fieldName && c.operator);
      if (criteria.length < 1) {
        throw new Error(
          'Add at least one criterion row below. Numbers in the expression (1, 2, …) refer to those rows.'
        );
      }
      return {
        logic: 'CUSTOM',
        criteria,
        customLogic: text,
      };
    }

    const criteria = (criteriaList || [])
      .map(normalizeCriterionRow)
      .filter((c) => c.fieldName && c.operator);

    return {
      logic: criteriaLogic === 'OR' ? 'OR' : 'AND',
      criteria,
    };
  };

  const buildAccessPayload = () => {
    const rows = formAccess
      .filter((r) => r.target_id && String(r.target_id).trim())
      .map((r) => ({
        target_type: String(r.target_type || 'USER').toUpperCase(),
        target_id: String(r.target_id).trim(),
        access_level: normalizeAccessLevelForForm(r.access_level),
      }));
    return rows;
  };

  const handleSave = async () => {
    setSaving(true);
    setModalError('');
    try {
      const conditions_json = buildConditionsJson();
      const access = buildAccessPayload();
      if (!formObject) {
        throw new Error('Select an object');
      }
      const trimmedName = String(formRuleName || '').trim();
      if (!trimmedName) {
        throw new Error('Rule name is required');
      }
      const critCount = (conditions_json.criteria || []).length;
      if (critCount < 1) {
        throw new Error('At least one criterion with field and operator is required');
      }
      if (!access.length) {
        throw new Error('At least one access grant with a selected target is required');
      }
      const body = {
        object_name: formObject,
        name: trimmedName,
        rule_type: 'CRITERIA',
        conditions_json,
        is_active: formActive,
        priority: Number(formPriority) || 0,
        access,
      };
      if (editing) {
        const res = await api.updateRule(editing.id, body);
        if (!res?.data?.success) {
          throw new Error(res?.data?.message || 'Update failed');
        }
        setSuccess('Sharing rule updated');
      } else {
        const res = await api.createRule(body);
        if (!res?.data?.success) {
          throw new Error(res?.data?.message || 'Create failed');
        }
        setSuccess('Sharing rule created');
        const created = res?.data?.data;
        const newId = created?.id;
        setModalError('');
        setShowModal(false);
        await loadRules();
        if (newId && formActive) {
          openApplyModal({
            id: newId,
            name: trimmedName,
            object_name: formObject,
            conditions_json,
          });
        }
        return;
      }
      setModalError('');
      setShowModal(false);
      await loadRules();
    } catch (e) {
      setModalError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule) => {
    const label = (rule.name && String(rule.name).trim()) || rule.object_name;
    if (!window.confirm(`Delete sharing rule "${label}"?`)) return;
    setError('');
    try {
      const res = await api.deleteRule(rule.id);
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || 'Delete failed');
      }
      setSuccess('Rule deleted');
      await loadRules();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Delete failed');
    }
  };

  const addAccessRow = () => setFormAccess([...formAccess, emptyAccessRow()]);
  const removeAccessRow = (idx) => {
    const next = formAccess.filter((_, i) => i !== idx);
    setFormAccess(next.length ? next : [emptyAccessRow()]);
  };
  const patchAccess = (idx, field, value) => {
    const next = [...formAccess];
    if (field === 'target_type') {
      next[idx] = {
        ...next[idx],
        target_type: value,
        target_id: '',
        target_label: '',
      };
    } else {
      next[idx] = { ...next[idx], [field]: value };
    }
    setFormAccess(next);
  };

  const setGrantLookupSelection = (idx, id, label) => {
    const next = [...formAccess];
    next[idx] = {
      ...next[idx],
      target_id: id || '',
      target_label: label || '',
    };
    setFormAccess(next);
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0 d-flex align-items-center gap-2">
            <FaShareAlt /> Sharing Rules
          </h4>
          <small className="text-muted">
            Criteria-based grants (same field/operator builder as workflows). Custom logic: set Criteria
            Logic to Custom, enter an expression (1, 2, …), and define matching rows below — then Apply
            evaluates combined logic against records.
          </small>
        </Col>
        <Col xs="auto" className="d-flex gap-2 flex-wrap justify-content-end">
          <Form.Select
            style={{ width: 200 }}
            value={filterObject}
            onChange={(e) => setFilterObject(e.target.value)}
          >
            <option value="all">All Objects</option>
            {objects.map((o) => (
              <option key={o.name} value={o.name}>
                {o.label}
              </option>
            ))}
          </Form.Select>
          <Form.Check
            type="switch"
            id="active-only"
            label="Active Only"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="d-flex align-items-center"
          />
          <Button variant="primary" onClick={openCreate}>
            <FaPlus className="me-1" /> New Rule
          </Button>
        </Col>
      </Row>

      {error ? <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert> : null}
      {success ? <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert> : null}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Object</th>
                  <th>Rule Name</th>
                  <th>Priority</th>
                  <th>Criteria</th>
                  <th>Grants</th>
                  <th>Status</th>
                  <th style={{ width: 120 }} />
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      No sharing rules yet. Create one to define criteria and who receives Read, Read/Write, or full access.
                    </td>
                  </tr>
                ) : (
                  rules.map((r) => {
                    const grants = Array.isArray(r.access_entries) ? r.access_entries.length : 0;
                    const crit = countCriteriaFromRule(r);
                    const isCustomRule = isCustomSharingConditionsJson(r);
                    const applyBlocked = crit === 'custom' || !r.is_active;
                    const criteriaLabel =
                      crit === 'custom'
                        ? 'Custom (add rows)'
                        : isCustomRule
                          ? `Custom (${crit} row${crit === 1 ? '' : 's'})`
                          : typeof crit === 'number'
                            ? `${crit} row${crit === 1 ? '' : 's'}`
                            : 'Other';
                    return (
                      <tr key={r.id}>
                        <td>{r.object_name}</td>
                        <td>{(r.name && String(r.name).trim()) || '—'}</td>
                        <td>{r.priority}</td>
                        <td>{criteriaLabel}</td>
                        <td>{grants}</td>
                        <td>
                          {r.is_active ? (
                            <Badge bg="success">Active</Badge>
                          ) : (
                            <Badge bg="secondary">Inactive</Badge>
                          )}
                        </td>
                        <td className="text-end text-nowrap">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1"
                            onClick={() => openApplyModal(r)}
                            title={
                              applyBlocked
                                ? crit === 'custom'
                                  ? 'Add criterion rows to this custom rule before applying'
                                  : !r.is_active
                                    ? 'Activate the rule before applying'
                                    : 'Apply to matching records'
                                : 'Apply to matching records'
                            }
                            disabled={applyBlocked}
                          >
                            <FaSyncAlt />
                          </Button>
                          <Button variant="link" size="sm" className="p-1" onClick={() => openEdit(r)} title="Edit">
                            <FaEdit />
                          </Button>
                          <Button variant="link" size="sm" className="p-1 text-danger" onClick={() => handleDelete(r)} title="Delete">
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={closeModal} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit Sharing Rule' : 'New Sharing Rule'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError ? (
            <Alert variant="danger" dismissible onClose={() => setModalError('')} className="mb-3">
              {modalError}
            </Alert>
          ) : null}
          <Row className="g-2 mb-3">
            <Col md={5}>
              <Form.Label>
                Rule Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={formRuleName}
                onChange={(e) => setFormRuleName(e.target.value)}
                placeholder="e.g. Share open leads with inside sales"
                maxLength={255}
                required
              />
            </Col>
            <Col md={5}>
              <Form.Label>
                Object <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select value={formObject} onChange={(e) => setFormObject(e.target.value)} required>
                {objects.length === 0 ? (
                  <option value="">No objects</option>
                ) : null}
                {objects.map((o) => (
                  <option key={o.name} value={o.name}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Priority</Form.Label>
              <Form.Control
                type="number"
                value={formPriority}
                onChange={(e) => setFormPriority(e.target.value)}
              />
            </Col>
          </Row>
          <Form.Check
            type="switch"
            className="mb-3"
            label="Active"
            checked={formActive}
            onChange={(e) => setFormActive(e.target.checked)}
          />

          <hr />
          <h6 className="mb-2">Match Conditions</h6>
          <Row>
            <Col md={4}>
              <SharingCriteriaLogicSelect
                value={criteriaLogic}
                onChange={onSharingCriteriaLogicChange}
                readOnly={false}
              />
            </Col>
          </Row>
          {criteriaLogic === 'CUSTOM' && (
            <Form.Group className="mb-2 mt-1">
              <Form.Label>
                Expression <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={customLogic}
                onChange={(e) => setCustomLogic(e.target.value)}
                placeholder="(1 OR 2) AND 3"
                className="font-monospace small"
              />
              <Form.Text muted>
                Row numbers 1…n refer to the criteria rows below. Use AND, OR, and parentheses — same
                as workflows and list view filters.
              </Form.Text>
            </Form.Group>
          )}
          <SharingRuleCriteriaConfig
            criteriaList={criteriaList}
            onAdd={addCriterion}
            onDelete={deleteCriterion}
            onChange={updateCriterion}
            onPatch={patchCriterion}
            objectFields={objectFields}
            valueFields={
              criteriaValueFields?.length ? criteriaValueFields : objectFields
            }
            readOnly={false}
            disabled={!formObject}
          />

          <hr />
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="mb-0 fw-semibold">Access Grants</Form.Label>
            <Button variant="outline-secondary" size="sm" onClick={addAccessRow}>
              Add Grant
            </Button>
          </div>
          {formAccess.map((row, idx) => {
            const lookup = grantLookupMeta(row.target_type);
            const displayLabel = String(row.target_label || '').trim();
            return (
            <Row key={`grant-${idx}-${row.target_type}`} className="g-2 mb-2 align-items-start sharing-rule-grant-row">
              <Col md={3}>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-1">Target Type</Form.Label>
                  <Form.Select
                    style={standardInputStyles}
                    value={row.target_type}
                    onChange={(e) => patchAccess(idx, 'target_type', e.target.value)}
                  >
                    <option value="USER">User</option>
                    <option value="ROLE">Role</option>
                    <option value="TEAM">User Group (Team)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-1">Grant To</Form.Label>
                  <div style={standardInputContainerHeight}>
                    <LookupLayout
                      key={`${idx}-${row.target_type}`}
                      rootClassName="sharing-rule-grant-lookup"
                      objectName={lookup.objectName}
                      searchFields={lookup.searchFields}
                      displayFields={lookup.displayFields}
                      value={row.target_id || ''}
                      defaultLabel={displayLabel}
                      onChange={(id, label) => setGrantLookupSelection(idx, id, label)}
                      placeholder={lookup.placeholder}
                      activeUsersOnly={lookup.activeUsersOnly || row.target_type === 'USER'}
                      lookupFilter={lookup.lookupFilter || null}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-1">Access</Form.Label>
                  <Form.Select
                    style={standardInputStyles}
                    value={row.access_level}
                    onChange={(e) => patchAccess(idx, 'access_level', e.target.value)}
                  >
                    {SHARING_ACCESS_LEVEL_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={1} className="d-flex flex-column align-items-end">
                <Form.Label className="small mb-1 invisible text-nowrap" aria-hidden>
                  —
                </Form.Label>
                <Button variant="outline-danger" size="sm" onClick={() => removeAccessRow(idx)}>
                  ×
                </Button>
              </Col>
            </Row>
            );
          })}
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-between">
          <div>
            {editing ? (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const r = editing;
                  closeModal();
                  openApplyModal(r);
                }}
                disabled={saving || !formActive}
                title={!formActive ? 'Activate the rule before applying' : ''}
              >
                <FaSyncAlt className="me-1" /> Apply to Records
              </Button>
            ) : null}
          </div>
          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={applyModal.show} onHide={closeApplyModal} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Apply Sharing to Records</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {applyError ? (
            <Alert variant="danger" dismissible onClose={() => setApplyError('')} className="mb-3">
              {applyError}
            </Alert>
          ) : null}
          <p className="mb-2">
            This evaluates criteria for <strong>{applyModal.objectName}</strong> and writes{' '}
            <code>record_access</code> rows for each grant (users expanded from roles and groups). Existing rows for
            this rule are replaced; manual shares are not overwritten.
          </p>
          <p className="text-muted small mb-0">
            Rule: <strong>{applyModal.ruleName}</strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeApplyModal} disabled={applyLoading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={runApplySharing} disabled={applyLoading}>
            {applyLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" /> Applying…
              </>
            ) : (
              <>
                <FaSyncAlt className="me-1" /> Run
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SharingRuleManager;
